import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';

import './monobank-token-section.css';
// The action-button classes live with the other dashboard dialogs' styles.
// Native elements instead of MUI: the shared theme is built for the dark
// mobile app and renders TextField/Button unreadable on the light dashboard.
import '../../../pages/DashboardV2/Roles/roles.css';
import { MONOBANK_TOKEN_MIN_LENGTH } from '../../../constants/organization';
import {
  useMyOrganizationQuery,
  useRemoveMonobankTokenMutation,
  useSetMonobankTokenMutation,
} from '../../../hooks/useDashboardQueries';
import { parseApiError } from '../../../utils/parseApiError';

interface Feedback {
  kind: 'success' | 'error';
  text: string;
}

/**
 * Organization owners connect their Monobank acquiring token here.
 * The token is write-only: the API returns only a masked hint, never the token
 * itself, so nothing entered in the input is ever displayed or persisted client-side.
 */
export const MonobankTokenSection = () => {
  const { t } = useTranslation();
  const { data: organization, isLoading } = useMyOrganizationQuery();
  const setMutation = useSetMonobankTokenMutation();
  const removeMutation = useRemoveMonobankTokenMutation();

  const [token, setToken] = useState('');

  // Self-hides for callers who own no organization (query resolves to null on 404).
  if (isLoading || !organization) {
    return null;
  }

  const busy = setMutation.isPending || removeMutation.isPending;

  // Feedback is derived from mutation state (no mirrored local state); each
  // handler resets the sibling mutation so only one result is visible.
  const handleSave = async () => {
    removeMutation.reset();
    try {
      await setMutation.mutateAsync(token.trim());
      setToken('');
    } catch { /* surfaced via setMutation.error below */ }
  };

  const handleRemove = async () => {
    setMutation.reset();
    try {
      await removeMutation.mutateAsync();
    } catch { /* surfaced via removeMutation.error below */ }
  };

  const errorFallback = t('dashboard.settings.monobank.invalidToken');
  const failedMutation = [setMutation, removeMutation].find((m) => m.isError);
  let feedback: Feedback | null = null;
  if (failedMutation) {
    feedback = { kind: 'error', text: parseApiError(failedMutation.error, errorFallback).message };
  } else if (setMutation.isSuccess) {
    feedback = { kind: 'success', text: t('dashboard.settings.monobank.saved') };
  } else if (removeMutation.isSuccess) {
    feedback = { kind: 'success', text: t('dashboard.settings.monobank.removed') };
  }

  return (
    <Stack gap={1.5} className="monobank-section">
      <Stack gap={0.5}>
        <Typography className="monobank-title">
          {t('dashboard.settings.monobank.title')}
        </Typography>
        <Typography className="monobank-subtitle monobank-text-muted">
          {t('dashboard.settings.monobank.subtitle', { name: organization.name })}
        </Typography>
      </Stack>

      <Typography sx={{ fontSize: 13 }}>
        {organization.hasMonobankToken ? (
          <>
            <Box component="span" className="monobank-status-connected">
              {t('dashboard.settings.monobank.connected')}
            </Box>{' '}
            <Box component="code" className="monobank-token-hint">
              {organization.monobankTokenHint}
            </Box>
          </>
        ) : (
          <Box component="span" className="monobank-text-muted">
            {t('dashboard.settings.monobank.notConnected')}
          </Box>
        )}
      </Typography>

      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="flex-start">
        <input
          type="password"
          autoComplete="off"
          className="filter-search-input"
          placeholder={t('dashboard.settings.monobank.placeholder')}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          disabled={busy}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button
          type="button"
          className="primary-action-btn"
          disabled={busy || token.trim().length < MONOBANK_TOKEN_MIN_LENGTH}
          onClick={handleSave}
        >
          {t('dashboard.settings.monobank.save')}
        </button>
        {organization.hasMonobankToken && (
          <button
            type="button"
            className="danger-action-btn"
            disabled={busy}
            onClick={handleRemove}
          >
            {t('dashboard.settings.monobank.remove')}
          </button>
        )}
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.kind === 'error' ? 'error' : 'success'}
          role={feedback.kind === 'error' ? 'alert' : 'status'}
          sx={{ fontSize: 13 }}
        >
          {feedback.text}
        </Alert>
      )}
    </Stack>
  );
};
